import { useQuery } from '@apollo/client';
import useMessages from 'hooks/useMessages';
import { APP_QUERY_PARAMS } from 'utils/queryParam.utils';
import { GET_CHAINS } from 'graphql/secure/queries/chain.queries';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IChainsResponse } from 'FeatureModules/ComponentChain/types/chain.types';
import { StringParam, useQueryParam } from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';
import useComponentChainActions from './useComponentChainActions';
import { COMPONENT_CHAINS_PER_PAGE } from 'utils/pagination.utils';

interface IComponentChainHookProps {
  // Can be used to skip the useQueryParam search and use the internal search term instead
  skipUseQueryParamSearch?: boolean;
  queryAllComponentChains?: boolean;
}

const useComponentChains = (
  { skipUseQueryParamSearch, queryAllComponentChains }: IComponentChainHookProps = {
    skipUseQueryParamSearch: false,
    queryAllComponentChains: false,
  }
) => {
  const navigate = useNavigate();
  const { setErrorMessage } = useMessages();

  const [searchTerm = '', setSearchTerm] = useQueryParam(APP_QUERY_PARAMS.searchTerm, StringParam);
  const [internalSearchTerm, setInternalSearchTerm] = useState<string | null>(searchTerm);

  const { data, loading, error, fetchMore } = useQuery<IChainsResponse>(GET_CHAINS, {
    variables: {
      filters: {
        chainType: 'COMPONENT',
        searchTerm: internalSearchTerm?.length ? internalSearchTerm : undefined,
      },
      first: queryAllComponentChains ? null : COMPONENT_CHAINS_PER_PAGE,
    },
  });

  const hasNextPage: boolean = !!data?.chains?.pageInfo?.hasNextPage;
  const endCursor = data?.chains?.pageInfo?.endCursor;

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const componentChains = useMemo(
    () => (data?.chains ? removeGraphConnections(data.chains) : []),
    [data]
  );

  const { createComponentChain } = useComponentChainActions();

  // Keep states in sync
  useEffect(() => setInternalSearchTerm(searchTerm), [searchTerm]);

  const handleCreateComponentChain = async () => {
    try {
      const result = await createComponentChain();
      if (result?.data?.createChain?.chain?.id) {
        navigate(`/component-chains/${result.data.createChain.chain.id}/editor`);
      } else {
        setErrorMessage(`Failed to create new component chain. No component chain id returned.`);
      }
    } catch (error: unknown) {
      setErrorMessage(`Failed to create new component chain. ${error}`);
    }
  };

  const handleSearchTermChange = skipUseQueryParamSearch ? setInternalSearchTerm : setSearchTerm;

  return {
    searchTerm: internalSearchTerm,
    componentChains,
    data,
    loading,
    error,
    hasNextPage,
    handlePageEndReached,
    handleCreateComponentChain,
    setSearchTerm: handleSearchTermChange,
  };
};

export default useComponentChains;
